<script setup>
import { ref, onMounted, watch, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useKanban } from 'dashboard/composables/useKanban';
import { useAccount } from 'dashboard/composables/useAccount';
import KanbanView from './KanbanView.vue';
import KanbanPipelineList from './KanbanPipelineList.vue';
import KanbanPipelineModal from './components/KanbanPipelineModal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { fetchPipelines, createPipeline } = useKanban();

const currentView = ref('loading'); // 'loading', 'board', 'list'
const selectedPipelineId = ref(null);
const pipelines = ref([]);
const currentPipeline = ref(null);
const showPipelineDropdown = ref(false);
const showCreateModal = ref(false);

const loadInitialState = async () => {
  try {
    const pipelineData = await fetchPipelines();
    pipelines.value = Array.isArray(pipelineData) ? pipelineData : [];

    // Se estamos na rota de pipelines, sempre mostra a lista
    if (route.name === 'kanban_pipelines_index') {
      currentView.value = 'list';
    }
    // Se já tem pipelineId na rota, usa ele
    else if (route.params.pipelineId) {
      selectedPipelineId.value = route.params.pipelineId;
      currentPipeline.value =
        pipelines.value.find(p => p.id == route.params.pipelineId) || null;
      currentView.value = 'board';
    }
    // Se acessado via /board sem pipelineId, tenta abrir o primeiro funil
    else if (
      route.name === 'kanban_board_index' &&
      pipelines.value.length > 0
    ) {
      selectedPipelineId.value = pipelines.value[0].id;
      currentPipeline.value = pipelines.value[0];
      currentView.value = 'board';

      // Atualiza a URL para refletir o funil selecionado
      router.replace({
        name: 'kanban_pipeline_board',
        params: {
          ...route.params,
          pipelineId: selectedPipelineId.value,
        },
        query: route.query,
      });
    } else {
      // Caso contrário, mostra a lista
      currentView.value = 'list';
    }
  } catch (error) {
    console.error('Error loading pipelines:', error);
    currentView.value = 'list';
  }
};

const switchPipeline = pipelineId => {
  selectedPipelineId.value = pipelineId;
  currentPipeline.value = pipelines.value.find(p => p.id == pipelineId) || null;
  router.push({
    name: 'kanban_pipeline_board',
    params: {
      ...route.params,
      pipelineId,
    },
    query: route.query,
  });
};

const selectPipeline = pipeline => {
  showPipelineDropdown.value = false;
  switchPipeline(pipeline.id);
};

const createNewPipeline = () => {
  showPipelineDropdown.value = false;
  showCreateModal.value = true;
};

const handleCreatePipeline = async formData => {
  try {
    const newPipeline = await createPipeline(formData);

    // Atualizar lista de pipelines
    await loadInitialState();

    // Se estiver na view de board, trocar para o novo pipeline
    if (currentView.value === 'board') {
      selectedPipelineId.value = newPipeline.id;
      currentPipeline.value = newPipeline;
      router.push({
        name: 'kanban_pipeline_board',
        params: {
          ...route.params,
          pipelineId: newPipeline.id,
        },
        query: route.query,
      });
    }

    showCreateModal.value = false;
  } catch (error) {
    console.error('Error creating pipeline:', error);
    // TODO: Show error message to user
  }
};

const handleCloseModal = () => {
  showCreateModal.value = false;
};

const handleCreatePipelineEvent = () => {
  showCreateModal.value = true;
};

// Watch para mudanças na rota
watch(
  () => route.params.pipelineId,
  newPipelineId => {
    if (newPipelineId && newPipelineId !== selectedPipelineId.value) {
      selectedPipelineId.value = newPipelineId;
      currentPipeline.value =
        pipelines.value.find(p => p.id == newPipelineId) || null;
      currentView.value = 'board';
    }
  }
);

// Fechar dropdown ao clicar fora
const handleClickOutside = event => {
  const dropdownElement = event.target.closest('[data-pipeline-dropdown]');
  if (!dropdownElement && showPipelineDropdown.value) {
    showPipelineDropdown.value = false;
  }
};

onMounted(() => {
  loadInitialState();
  document.addEventListener('click', handleClickOutside);

  // Listener para criar novo pipeline
  window.addEventListener('kanban-create-pipeline', handleCreatePipelineEvent);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener(
    'kanban-create-pipeline',
    handleCreatePipelineEvent
  );
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Loading state -->
    <div
      v-if="currentView === 'loading'"
      class="h-full flex items-center justify-center"
    >
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-slate-9 mx-auto"
        />
        <p class="text-n-slate-11 mt-2">{{ t('KANBAN.LOADING_PIPELINES') }}</p>
      </div>
    </div>

    <!-- Board view -->
    <div
      v-else-if="currentView === 'board'"
      class="flex flex-col w-full h-full"
    >
      <!-- Kanban board -->
      <div class="flex-1 overflow-hidden">
        <KanbanView :key="selectedPipelineId" />
      </div>
    </div>

    <!-- List view (fallback) -->
    <div v-if="currentView === 'list'" class="w-full h-full">
      <KanbanPipelineList @create-pipeline="handleCreatePipelineEvent" />
    </div>

    <!-- Create Pipeline Modal -->
    <KanbanPipelineModal
      v-if="showCreateModal"
      :pipeline="null"
      :is-edit="false"
      @close="handleCloseModal"
      @save="handleCreatePipeline"
    />
  </div>
</template>
